<template>
    <div class="device-commands-component">
        <Loader v-if="loading" />
        <Protector v-if="sending" />
        <b-alert variant="danger" :show="loadError">
            {{$t('command_load_error')}}
            <b-button size="sm" @click="loadDeviceCommands($props.device)">{{$t('retry')}}</b-button>
        </b-alert>
        <b-alert variant="success" :show="isOk()" dismissible>{{$t('monitoring.commands.execution_success')}}</b-alert>
        <b-alert variant="danger" :show="isError()" dismissible>{{$t('monitoring.commands.execution_failed')}}</b-alert>
        <h6 v-if="!loading && !loadError && !commandList.length">{{$t('monitoring.commands.no_commands')}}</h6>
        <ul class="command-list" v-if="!loading && commandList.length">
            <li class="command-list-item" v-bind:key="command.identity" v-for="command in commandList">
                <div class="icon" @click="execute(command.identity)">
                    <b-icon-play-circle scale="1.5" />
                </div>
                <span>{{$t(command.name)}}</span>
            </li>
        </ul>
    </div>
</template>

<script lang="ts">
import Vue from "vue";
import Component from "vue-class-component";
import {
    DeviceCommandDto, CommandExecutionStatus,
    DeviceCommandsRepository
} from "../../Repository/DeviceCommands/DeviceCommandsRepository";
import {Watch} from "vue-property-decorator";
import Loader from "../Common/Loader.vue";
import Protector from "../Common/Protector.vue";

enum ExecutionResult {
    OK,
    ERROR
}

@Component({
    components: { Loader, Protector },
    props: {
        device: { type: Number, required: true }
    }
})
class DeviceCommands extends Vue {
    commandList: DeviceCommandDto[] = [];
    loading: boolean = false;
    sending: boolean = false;
    executionResult: ExecutionResult|null = null;
    loadError: boolean = false;
    listRequestId: number = 0;
    executionRequestId: number = 0;
    disposed: boolean = false;

    beforeDestroy() {
        this.disposed = true;
        this.listRequestId++;
        this.executionRequestId++;
    }

    mounted() {
        this.loadDeviceCommands(this.$props.device)
    }

    @Watch('device')
    deviceChanged(value: number) {
        this.loadDeviceCommands(value);
    }

    loadDeviceCommands(deviceId: number) {
        if (this.disposed) return;
        const requestId = ++this.listRequestId;
        this.executionRequestId++;
        this.sending = false;
        this.executionResult = null;
        this.commandList = [];
        this.loadError = false;
        this.loading = true;
        return DeviceCommandsRepository
            .getAllByDevice(deviceId)
            .then(response => {
                if (requestId !== this.listRequestId || this.disposed) return;
                this.commandList = response?.commands ?? [];
            })
            .catch(() => {
                if (requestId === this.listRequestId && !this.disposed) this.loadError = true;
            })
            .finally(() => {
                if (requestId === this.listRequestId && !this.disposed) this.loading = false;
            });
    }

    execute(identity: string): void {
        if (this.disposed || this.loading || this.sending || !this.commandList.some(command => command.identity === identity)) return;
        const deviceId = this.$props.device;
        const requestId = ++this.executionRequestId;
        this.executionResult = null;
        this.sending = true;
        DeviceCommandsRepository
            .executeCommand(deviceId, identity)
            .then(response => {
                if (requestId !== this.executionRequestId || this.disposed) return;
                this.executionResult = response?.status === CommandExecutionStatus.OK ? ExecutionResult.OK : ExecutionResult.ERROR;
            })
            .catch(() => {
                if (requestId === this.executionRequestId && !this.disposed) this.executionResult = ExecutionResult.ERROR;
            })
            .finally(() => {
                if (requestId === this.executionRequestId && !this.disposed) this.sending = false;
            });
    }

    isOk(): boolean {
        return this.executionResult === ExecutionResult.OK;
    }

    isError(): boolean {
        return this.executionResult === ExecutionResult.ERROR;
    }
}

export default DeviceCommands;
</script>

<style lang="scss" scoped>
    @import '../../custom1.scss';

    .device-commands-component {
        position: relative;

        .command-list {
            padding: 0;
            margin: 0;
            list-style: none;

            .command-list-item {
                display: flex;
                flex-direction: row;
                padding: 0.5em 0;

                &:hover {
                    background-color: $primary;
                }

                .icon {
                    padding: 0 1em 0 0.5em;
                    cursor: pointer;
                }
            }
        }
    }
</style>
